import { useState, lazy } from "react";
import { useTranslation } from "react-i18next";
import { Hero } from "@/components/Hero";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { CheckCircle2, Shield, Play } from "lucide-react";
import { ScrollReveal } from "@/hooks/use-scroll-animation";
import { LazyOnVisible } from "@/components/LazyOnVisible";
import { CookieConsent } from "@/components/CookieConsent";

// These are NOT imported at module level — they only load when LazyOnVisible triggers
const DonationForm = lazy(() => import("@/components/DonationForm").then(m => ({ default: m.DonationForm })));
const ContactForm = lazy(() => import("@/components/ContactForm").then(m => ({ default: m.ContactForm })));

const Index = () => {
  const { t } = useTranslation();
  const [videoLoaded, setVideoLoaded] = useState(false);
  return (
    <main className="min-h-screen bg-background">
      <SEO />
      <Hero />
      
      {/* Brief Why Section — Sacred Geometry */}
      <section className="py-12 md:py-16 bg-background relative">
        <div className="container mx-auto px-4 max-w-5xl">
          {/* Gold Divider Above */}
          <div className="gold-divider mb-10 lg:mb-12" />

          <ul className="grid md:grid-cols-3 gap-4 lg:gap-6">
            {[
              { key: 'reason1', delay: 0 },
              { key: 'reason2', delay: 100 },
              { key: 'reason3', delay: 200 },
            ].map(({ key, delay }) => (
              <ScrollReveal key={key} delay={delay} direction="up" as="li"
                className="group relative bg-background/95 backdrop-blur-lg rounded-2xl p-5 lg:p-6 shadow-card border border-border/40 hover:shadow-elegant transition-all duration-300 hover:-translate-y-1"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-10 h-10 hex-clip bg-secondary/15 flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-secondary" />
                  </div>
                  <span className="text-foreground font-medium leading-snug pt-2">
                    {t(`why.${key}`)}
                  </span>
                </div>
              </ScrollReveal>
            ))}
          </ul>
        </div>
      </section>

      {/* Donation Section — Sacred Geometry */}
      <section id="donate" className="py-16 sm:py-20 md:py-28 bg-muted/40 relative hex-pattern">
        {/* Subtle Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-[100px]" />
          <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-secondary/5 rounded-full blur-[80px]" />
        </div>

        <div className="container relative mx-auto px-4">
          {/* Section Header */}
          <ScrollReveal direction="up">
            <div className="text-center mb-10 lg:mb-14">
              <span className="font-technical text-xs uppercase tracking-[0.2em] text-secondary mb-3 block">
                {t('common.donate')}
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-normal text-foreground mb-3 tracking-wide">
                {t('donation.sectionTitle')}
              </h2>
              <div className="gold-divider max-w-xs mx-auto mb-4" />
              <p className="text-base lg:text-lg text-muted-foreground max-w-xl mx-auto leading-relaxed">
                {t('donation.sectionDescription')}
              </p>
            </div>
          </ScrollReveal>

          <div className="grid gap-6 lg:gap-8 lg:grid-cols-2 max-w-6xl mx-auto">
            {/* Left Column - Form */}
            <div className="space-y-6">
              <ScrollReveal delay={100} direction="left">
                <div className="bg-background/95 backdrop-blur-lg rounded-2xl p-5 md:p-7 shadow-card border border-border/40">
                  <h3 className="text-xl font-display font-normal mb-5 text-foreground tracking-wide">
                    {t('donation.form.selectAmount')}
                  </h3>
                  <LazyOnVisible fallback={<div className="h-96 animate-pulse bg-muted rounded-xl" />}>
                    <DonationForm onSuccess={() => {}} />
                  </LazyOnVisible>
                </div>
              </ScrollReveal>
            </div>

            {/* Right Column - Video & Info */}
            <div className="space-y-6">
              {/* Video Section */}
              <ScrollReveal delay={150} direction="right">
                <div id="video" className="relative group">
                  <div className="absolute -inset-1 bg-gradient-to-br from-primary via-primary/80 to-secondary/30 rounded-3xl blur opacity-20 group-hover:opacity-30 transition-opacity" />
                  <div className="relative bg-primary rounded-2xl p-6 md:p-8 shadow-xl overflow-hidden hex-pattern">
                    {/* Decorative Element */}
                    <div className="absolute top-0 right-0 w-32 h-32 bg-secondary/10 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2" />
                    
                    {/* Gold accent line at top */}
                    <div className="absolute top-0 left-0 right-0 h-px gold-divider" />
                    
                    <div className="relative">
                      <div className="flex items-center gap-2 mb-4 rtl:flex-row-reverse">
                        <Play className="w-5 h-5 text-primary-foreground/80 fill-current" />
                        <h3 className="text-xl md:text-2xl font-display font-normal text-primary-foreground tracking-wide">
                          {t('donation.watchVideo')}
                        </h3>
                      </div>
                      
                      <div className="aspect-video rounded-xl overflow-hidden shadow-2xl ring-1 ring-white/10">
                        {videoLoaded ? (
                          <iframe
                            width="100%"
                            height="100%"
                            src="https://www.youtube-nocookie.com/embed/MXNk54kIw_k?autoplay=1"
                            title="Chabad of Flushing Building Campaign"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            className="w-full h-full"
                          />
                        ) : (
                          <button
                            onClick={() => setVideoLoaded(true)}
                            className="relative w-full h-full cursor-pointer group/play bg-foreground"
                            aria-label="Play video: Chabad of Flushing Building Campaign"
                          >
                            <img
                              src="https://img.youtube.com/vi/MXNk54kIw_k/hqdefault.jpg"
                              alt="Chabad of Flushing Building Campaign video thumbnail"
                              loading="lazy"
                              className="w-full h-full object-cover opacity-80 group-hover/play:opacity-100 transition-opacity"
                            />
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="w-16 h-16 md:w-20 md:h-20 bg-secondary rounded-full flex items-center justify-center shadow-lg group-hover/play:scale-110 transition-transform">
                                <Play className="w-8 h-8 md:w-10 md:h-10 text-secondary-foreground fill-current ml-1" />
                              </div>
                            </div>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </ScrollReveal>

              {/* Tax Notice */}
              <ScrollReveal delay={250} direction="right">
                <div className="bg-background/95 backdrop-blur-lg rounded-2xl p-5 md:p-6 shadow-card border border-secondary/10">
                  <div className="flex items-start gap-4 rtl:flex-row-reverse">
                    <div className="flex-shrink-0 w-10 h-10 hex-clip bg-secondary/15 flex items-center justify-center">
                      <Shield className="w-5 h-5 text-secondary" />
                    </div>
                    <div className="ltr:text-left rtl:text-right">
                      <h3 className="text-base font-semibold text-foreground mb-1">{t('common.taxDeductible')}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {t('donation.taxDeductible')}
                      </p>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <ScrollReveal direction="up">
        <LazyOnVisible fallback={<div className="h-64 animate-pulse bg-muted" />}>
          <ContactForm />
        </LazyOnVisible>
      </ScrollReveal>

      <Footer />
      <CookieConsent />
    </main>
  );
};

export default Index;
